import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MealPlanner from './MealPlanner';
import { processExcelFile } from '../utils/excelProcessor.js';
import { processRecipeData } from '../utils/recipeProcessor';

// const API_BASE_URL = 'https://lively-crostata-509cae.netlify.app';
const API_BASE_URL = 'https://app.wholenutritionservices.com';

export default function AdminDashboard({ onLogin }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('planner');
  const [selectedExcelFile, setSelectedExcelFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadedMeals, setUploadedMeals] = useState(null);

  const handleExcelFileChange = (event) => {
    console.log('Excel file selected:', event.target.files[0]?.name);
    setSelectedExcelFile(event.target.files[0]);
    setUploadStatus('');
    setUploadedMeals(null);
  };

  const handleUpdateMenu = async () => {
    if (!selectedExcelFile) {
      console.log('No file selected');
      setUploadStatus('Please select an Excel file first');
      return;
    }

    console.log('Starting menu update process...');
    setIsUploading(true);
    setUploadStatus('Processing Excel file...');
    setUploadedMeals(null);

    try {
      // Process Excel file for meals
      const mealData = await processExcelFile(selectedExcelFile);
      console.log('Meal data processed successfully');

      // Process Excel file for recipe instructions and bagging checklists
      const recipeData = await processRecipeData(selectedExcelFile);
      console.log(recipeData);
      console.log('Recipe instructions and bagging checklists processed successfully');

      setUploadStatus('Data processed, uploading to server...');

      // Upload meal data
      await fetch(`${API_BASE_URL}/upload-meals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mealData)
      });

      // Upload instructions data
      await fetch(`${API_BASE_URL}/upload-instructions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(recipeData.instructions)
      });

      // Upload bagging checklist data
      await fetch(`${API_BASE_URL}/upload-bagging`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(recipeData.baggingChecklists)
      });

      // Send processed JSON to server
      const response = await fetch(`${API_BASE_URL}/upload-meals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mealData)
      });

      console.log('Server response status:', response.status);
      const responseText = await response.text();
      console.log('Server response:', responseText);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status} - ${responseText}`);
      }

      setUploadStatus('Excel file processed and uploaded successfully');
      
      // Store uploaded meals to display
      setUploadedMeals({
        breakfast: mealData.breakfast?.map(meal => meal.name) || [],
        lunch: mealData.lunch?.map(meal => meal.name) || [],
        dinner: mealData.dinner?.map(meal => meal.name) || []
      });
    } catch (error) {
      console.error('Error processing Excel file:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            <div className="flex space-x-4">
              <button
                onClick={() => setActiveTab('menu')}
                className={`px-3 py-2 text-sm font-medium ${activeTab === 'menu' ? 'text-gray-900 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Menu
              </button>
              <button
                onClick={() => setActiveTab('planner')}
                className={`px-3 py-2 text-sm font-medium ${activeTab === 'planner' ? 'text-gray-900 border-b-2 border-indigo-500' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Meal Planner
              </button>
            </div>
            <button
              onClick={() => {
                onLogin(false);
                navigate('/admin');
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 px-4">
        {activeTab === 'planner' ? (
          <MealPlanner />
        ) : (
          <div className="bg-white shadow sm:rounded-lg p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Menu Management</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Excel File</label>
                    <p className="text-sm text-gray-500 mb-2">Upload an Excel file with Breakfast, Lunch, and Dinner sheets</p>
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleExcelFileChange}
                      className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    <button
                      onClick={handleUpdateMenu}
                      disabled={isUploading}
                      className={`mt-4 px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                    >
                      {isUploading ? 'Uploading...' : 'Upload Excel'}
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-500">
                  {uploadStatus && (
                    <p className={`mt-2 ${uploadStatus.includes('Success') ? 'text-green-600' : 'text-red-600'}`}>
                      {uploadStatus}
                    </p>
                  )}
                </div>
                
                {uploadedMeals && (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="font-medium text-gray-900 mb-2">Uploaded Meals Summary</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">Breakfast ({uploadedMeals.breakfast.length})</h5>
                        <ul className="mt-1 text-sm text-gray-600 list-disc pl-5">
                          {uploadedMeals.breakfast.map((meal, index) => (
                            <li key={`breakfast-${index}`}>{meal}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">Lunch ({uploadedMeals.lunch.length})</h5>
                        <ul className="mt-1 text-sm text-gray-600 list-disc pl-5">
                          {uploadedMeals.lunch.map((meal, index) => (
                            <li key={`lunch-${index}`}>{meal}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h5 className="text-sm font-medium text-gray-700">Dinner ({uploadedMeals.dinner.length})</h5>
                        <ul className="mt-1 text-sm text-gray-600 list-disc pl-5">
                          {uploadedMeals.dinner.map((meal, index) => (
                            <li key={`dinner-${index}`}>{meal}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}